import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import { useNotifications } from "@/context/NotificationContext";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Link } from "react-router-dom";
import api from "@/services/api";
import { convertDriveLink } from "@/utils/convertDriveLink";
import PortfolioModal from "./components/PortfolioModal";

const portfolioCategories = ["SaaS", "Fintech", "Trading", "AI Tools", "Automation", "Web App"];

interface PortfolioItem {
  id: string;
  title: string;
  category: string;
  description: string;
  thumbnail_url: string | null;
  tech_stack: string[];
  featured: boolean;
  year: string | null;
  client_name: string | null;
  duration: string | null;
  video_url: string | null;

  overview?: string | null;
  challenge?: string | null;
  solution?: string | null;
  live_url?: string | null;
  user_manual_url?: string | null;

  results?: { metric: string; value: string; description: string }[];
  gallery?: string[];
  tech_breakdown?: { layer: string; tools: string[] }[];
  collaborators?: string[];
}





export default function AdminPortfolio() {
  const { showToast } = useNotifications();
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterFeatured, setFilterFeatured] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [modalInitial, setModalInitial] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);


  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/portfolio");
      setItems(res.data.data || []);
    } catch {
      showToast("error", "Failed to load portfolio items");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const matchesCategory = (itemCategory: string, filterCategory: string) => {
    const item = itemCategory.trim().toLowerCase();
    const filter = filterCategory.trim().toLowerCase();
    if (item === filter || item.includes(filter) || filter.includes(item)) return true;
    const itemTokens = item.split(/[\s,/&-]+/).filter(t => t.length >= 3);
    const filterTokens = filter.split(/[\s,/&-]+/).filter(t => t.length >= 3);
    return filterTokens.some(ft => itemTokens.some(it => it.includes(ft) || ft.includes(it)));
  };

  const filtered = items.filter((item) => {
    const matchCat = filterCategory === "All" || matchesCategory(item.category, filterCategory);
    const matchFeat = !filterFeatured || item.featured;
    const matchSearch = !search ||
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      (item.client_name || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchFeat && matchSearch;
  });

  const openCreate = () => {
    setModalMode("create");
    setModalInitial(null);
    setShowModal(true);
  };
  
  const openEdit = (item: PortfolioItem) => {
    setModalMode("edit");
    setModalInitial(item);
    setShowModal(true);
  };

  const handleSubmit = async (payload: any) => {
    // Prevent accidental double-submit from modal.
    // PortfolioModal already disables submit while loading, but we add a guard here too.
    if ((handleSubmit as any)._busy) return;
    (handleSubmit as any)._busy = true;

    try {
      if (payload.id) {
        if (payload.id.startsWith("project-")) {
          // Edits for featured-project-backed items must ONLY update the project row.
          // Never call POST /portfolio from edit flow.
          const projectId = payload.id.replace("project-", "");
          await api.patch(`/projects/${projectId}`, { featured: payload.featured });
          showToast("info", "Project case-study updated (featured flag).");
          return;
        }

        // Edit existing portfolio_items row.
        await api.patch(`/portfolio/${payload.id}`, payload);
        showToast("success", `"${payload.title}" updated successfully.`);
      } else {
        // Create new portfolio_items row.
        await api.post("/portfolio", payload);
        showToast("success", `"${payload.title}" published.`);
      }

      fetchItems();
    } catch (error) {
      showToast("error", "Failed to save portfolio item.");
      throw error;
    } finally {
      (handleSubmit as any)._busy = false;
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/portfolio/${deleteId}`);
      showToast("success", "Item deleted successfully.");
      fetchItems();
    } catch {
      showToast("error", "Failed to delete item.");
    } finally {
      setDeleteId(null);
    }
  };

  const toggleFeatured = async (item: PortfolioItem) => {
    try {
      if (item.id.startsWith("project-")) {
        await api.patch(`/projects/${item.id.replace("project-", "")}`, { featured: !item.featured });
      } else {
        await api.patch(`/portfolio/${item.id}`, { featured: !item.featured });
      }
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, featured: !i.featured } : i));
      showToast("info", !item.featured ? "Marked as featured." : "Removed from featured.");
    } catch {
      showToast("error", "Failed to update featured status.");
    }
  };

  const featuredCount = items.filter((i) => i.featured).length;





  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Portfolio Management
            </h1>
            <p className="text-silver text-sm mt-1">
              {items.length} case studies · {featuredCount} featured
            </p>
          </div>
          <button 
            onClick={openCreate} 
            className="flex items-center gap-2 bg-electric-blue text-deep-black font-bold px-5 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Add Case Study
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Projects", value: items.length, icon: "ri-gallery-line", color: "text-electric-blue" },
            { label: "Featured", value: featuredCount, icon: "ri-star-line", color: "text-cyber-orange" },
            { label: "Categories", value: new Set(items.map((i) => i.category)).size, icon: "ri-price-tag-3-line", color: "text-cyber-green" },
            { label: "With Video", value: items.filter((i) => i.video_url).length, icon: "ri-play-circle-line", color: "text-cyber-red" },
          ].map((s) => (
            <div key={s.label} className="bg-dark-card border border-dark-border rounded-xl p-4 flex items-center gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-dark-panel border border-dark-border ${s.color}`}>
                <i className={`${s.icon} text-base`} />
              </div>
              <div>
                <div className="text-white font-black text-lg leading-none" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {s.value}
                </div>
                <div className="text-silver-dim text-xs mt-0.5">
                  {s.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-xs">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-silver-dim text-sm" />
            </div>
            <input 
              type="text" 
              placeholder="Search by title or client..." 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              className="w-full bg-dark-card border border-dark-border rounded-xl pl-9 pr-4 py-2.5 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors" 
            />
          </div>
          <div className="flex flex-wrap gap-2 flex-1">
            {portfolioCategories.map((cat) => (
              <button 
                key={cat} 
                onClick={() => setFilterCategory(cat)} 
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filterCategory === cat 
                    ? "bg-electric-blue text-deep-black" 
                    : "border border-dark-border text-silver hover:border-electric-blue/30 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button 
              onClick={() => setFilterFeatured(!filterFeatured)} 
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                filterFeatured 
                  ? "bg-cyber-orange/10 border-cyber-orange/30 text-cyber-orange" 
                  : "border-dark-border text-silver hover:border-cyber-orange/30"
              }`}
            >
              <i className="ri-star-line" />
              Featured
            </button>
            <div className="flex border border-dark-border rounded-lg overflow-hidden">
              <button 
                onClick={() => setViewMode("grid")} 
                className={`w-8 h-8 flex items-center justify-center transition-all cursor-pointer ${
                  viewMode === "grid" 
                    ? "bg-electric-blue/10 text-electric-blue" 
                    : "text-silver hover:text-white"
                }`}
              >
                <i className="ri-grid-line text-sm" />
              </button>
              <button 
                onClick={() => setViewMode("list")} 
                className={`w-8 h-8 flex items-center justify-center transition-all cursor-pointer ${
                  viewMode === "list" 
                    ? "bg-electric-blue/10 text-electric-blue" 
                    : "text-silver hover:text-white"
                }`}
              >
                <i className="ri-list-check text-sm" />
              </button>
            </div>
          </div>
        </div>

        <p className="text-silver-dim text-xs">
          Showing <strong className="text-white">{filtered.length}</strong> of {items.length} projects
          {filterCategory !== "All" && <> in <strong className="text-electric-blue">{filterCategory}</strong></>}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((item) => (
              <div key={item.id} className="group bg-dark-card border border-dark-border rounded-2xl overflow-hidden hover:border-electric-blue/20 transition-all duration-300">
                <div className="relative h-44 overflow-hidden">
                  {item.thumbnail_url ? (
                    <img 
                      src={convertDriveLink(item.thumbnail_url || "")} 
                      alt={item.title} 
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" 
                    />
                  ) : (
                    <div className="w-full h-full bg-dark-panel flex items-center justify-center">
                      <i className="ri-image-line text-3xl text-silver-dim" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-card/90 to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-dark-card/80 backdrop-blur-sm border border-dark-border text-electric-blue font-medium">
                      {item.category}
                    </span>
                    {item.featured && (
                      <span className="text-xs px-2.5 py-1 rounded-full bg-cyber-orange/80 text-white font-semibold">
                        Featured
                      </span>
                    )}
                  </div>
                  {item.video_url && (
                    <div className="absolute top-3 right-3">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-dark-card/80 backdrop-blur-sm border border-dark-border text-cyber-red">
                        <i className="ri-play-fill text-xs" />
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => openEdit(item)}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-dark-card/90 border border-dark-border text-silver transition-all cursor-pointer hover:text-electric-blue hover:border-electric-blue/40"
                    >
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button 
                      onClick={() => setDeleteId(item.id)} 
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-dark-card/90 border border-dark-border text-silver hover:text-cyber-red hover:border-cyber-red/40 transition-all cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-white font-bold text-sm leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {item.title}
                    </h3>
                    <span className="text-silver-dim text-xs flex-shrink-0">
                      {item.year}
                    </span>
                  </div>
                  <p className="text-silver-dim text-xs mb-1">
                    {item.client_name}
                  </p>
                  <p className="text-silver-dim text-xs leading-relaxed mb-3 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {(Array.isArray(item.tech_stack) ? item.tech_stack : []).slice(0, 3).map((t) => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded bg-deep-black border border-dark-border text-silver-dim">
                        {t}
                      </span>
                    ))}
                    {Array.isArray(item.tech_stack) && item.tech_stack.length > 3 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-deep-black border border-dark-border text-silver-dim">
                        +{item.tech_stack.length - 3}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => toggleFeatured(item)} 
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs transition-all cursor-pointer whitespace-nowrap ${
                        item.featured 
                          ? "border-cyber-orange/30 text-cyber-orange bg-cyber-orange/5" 
                          : "border-dark-border text-silver-dim hover:border-cyber-orange/30 hover:text-cyber-orange"
                      }`}
                    >
                      <i className={`${item.featured ? "ri-star-fill" : "ri-star-line"} text-xs`} />
                      {item.featured ? "Featured" : "Feature"}
                    </button>
                    <button
                      onClick={() => openEdit(item)}
                      className="flex-1 py-1.5 rounded-lg border border-dark-border text-silver transition-all cursor-pointer whitespace-nowrap text-xs text-center hover:text-white hover:border-electric-blue/40"
                    >
                      <i className="ri-edit-line mr-1" />
                      Edit
                    </button>
                    <Link 
                      to={`/portfolio/${item.id}`} 
                      target="_blank" 
                      className="flex items-center justify-center w-8 h-8 rounded-lg border border-dark-border text-silver-dim hover:text-electric-blue hover:border-electric-blue/40 transition-all cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-external-link-line text-xs" />
                    </Link>
                    <button 
                      onClick={() => setDeleteId(item.id)} 
                      className="flex items-center justify-center w-8 h-8 rounded-lg border border-dark-border text-silver-dim hover:text-cyber-red hover:border-cyber-red/30 transition-all cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <button 
              onClick={openCreate} 
              className="border-2 border-dashed border-dark-border rounded-2xl p-8 flex flex-col items-center justify-center gap-3 hover:border-electric-blue/30 transition-all cursor-pointer group min-h-64"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full border border-dark-border bg-dark-card group-hover:border-electric-blue/40 transition-all">
                <i className="ri-add-line text-xl text-silver-dim group-hover:text-electric-blue transition-colors" />
              </div>
              <div className="text-center">
                <p className="text-silver text-sm font-medium group-hover:text-white transition-colors">
                  Add Case Study
                </p>
                <p className="text-silver-dim text-xs mt-1">
                  Publish a new portfolio item
                </p>
              </div>
            </button>
          </div>
        ) : (
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border">
                  <th className="text-left text-silver-dim font-medium py-3 px-5 text-xs uppercase tracking-widest">
                    Project
                  </th>
                  <th className="text-left text-silver-dim font-medium py-3 px-4 text-xs uppercase tracking-widest hidden sm:table-cell">
                    Category
                  </th>
                  <th className="text-left text-silver-dim font-medium py-3 px-4 text-xs uppercase tracking-widest hidden md:table-cell">
                    Client
                  </th>
                  <th className="text-left text-silver-dim font-medium py-3 px-4 text-xs uppercase tracking-widest hidden lg:table-cell">
                    Year
                  </th>
                  <th className="text-left text-silver-dim font-medium py-3 px-4 text-xs uppercase tracking-widest hidden lg:table-cell">
                    Status
                  </th>
                  <th className="text-right text-silver-dim font-medium py-3 px-5 text-xs uppercase tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-b border-dark-border/50 hover:bg-dark-panel/40 transition-colors group">
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden border border-dark-border flex-shrink-0">
                          {item.thumbnail_url ? (
                            <img 
                              src={convertDriveLink(item.thumbnail_url || "")} 
                              alt={item.title} 
                              className="w-full h-full object-cover object-top" 
                            />
                          ) : (
                            <div className="w-full h-full bg-dark-panel flex items-center justify-center">
                              <i className="ri-image-line text-silver-dim text-sm" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-semibold truncate">
                            {item.title}
                          </p>
                          <p className="text-silver-dim text-xs truncate">
                            {(Array.isArray(item.tech_stack) ? item.tech_stack : []).slice(0, 3).join(", ")}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 hidden sm:table-cell">
                      <span className="text-xs px-2.5 py-1 rounded-full border border-electric-blue/20 bg-electric-blue/5 text-electric-blue">
                        {item.category}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-silver text-xs hidden md:table-cell">
                      {item.client_name}
                    </td>
                    <td className="py-3.5 px-4 text-silver-dim text-xs hidden lg:table-cell">
                      {item.year}
                    </td>
                    <td className="py-3.5 px-4 hidden lg:table-cell">
                      {item.featured ? (
                        <span className="text-xs px-2.5 py-1 rounded-full border border-cyber-orange/30 bg-cyber-orange/5 text-cyber-orange">
                          Featured
                        </span>
                      ) : (
                        <span className="text-xs px-2.5 py-1 rounded-full border border-dark-border text-silver-dim">
                          Standard
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button 
                          onClick={() => toggleFeatured(item)} 
                          className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all cursor-pointer ${
                            item.featured 
                              ? "border-cyber-orange/30 text-cyber-orange" 
                              : "border-dark-border text-silver-dim hover:text-cyber-orange"
                          }`}
                        >
                          <i className={`${item.featured ? "ri-star-fill" : "ri-star-line"} text-xs`} />
                        </button>
                        <button 
                          onClick={() => openEdit(item)} 
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-electric-blue hover:border-electric-blue/40 transition-all cursor-pointer"
                        >
                          <i className="ri-edit-line text-xs" />
                        </button>
                        <Link 
                          to={`/portfolio/${item.id}`} 
                          target="_blank" 
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-electric-blue hover:border-electric-blue/40 transition-all cursor-pointer"
                        >
                          <i className="ri-external-link-line text-xs" />
                        </Link>
                        <button 
                          onClick={() => setDeleteId(item.id)} 
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-cyber-red hover:border-cyber-red/30 transition-all cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-gallery-line text-3xl text-silver-dim mb-3 block" />
                <p className="text-silver text-sm">No projects match your filters</p>
              </div>
            )}
          </div>
        )}

        <ConfirmDialog
          open={!!deleteId}
          title="Delete Portfolio Item"
          message="Are you sure you want to delete this portfolio item? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />

        <PortfolioModal
          open={showModal}
          mode={modalMode}
          initial={modalInitial}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      </div>
    </AdminLayout>
  );
}

